import { useCallback, useEffect, useMemo, useState } from 'react';

import { Badge, Space, Typography } from 'antd';
import { useHistory, useLocation } from 'react-router-dom';

import { useAuth } from '../../../hooks/useAuth';
import { useListFetcher } from '../../../hooks/useListFetcher';
import { useSyncedSearch } from '../../../hooks/useSyncedSearch';
import { useUrlHydration } from '../../../hooks/useUrlHydration';
import { useAppDispatch, useAppSelector } from '../../../store';
import {
  fetchOvrProjects,
  hydrateFromUrl,
  OvrProjectsState,
  searchOvrProjectsByName,
} from '../../../store/features/ovrProjects/ovrProjectsSlice';
import { ListApiConfig } from '../../../types/list';
import { UserPermissions } from '../../../util';
import { buildListParams } from '../../../util/listApiParams';
import NoPermission from '../../elements/NoPermission';
import ViewWrapper from '../../elements/ViewWrapper';
import OVRContentRenderer from './components/OVRContentRenderer';
import OVRHeaderSection from './components/OVRHeaderSection';
import { OVRItemsListViewType } from './components/OVRProjectItemsList';
import './OnlineVirtualResearchView.less';

const { Title } = Typography;

const ovrListViewTypeKey = 'ovr-list-view-type';
const lastOvrProjectsItemLengthKey = 'last-ovr-projects-item-length-key';

const OnlineVirtualResearchView = () => {
  const { hasPermission } = useAuth();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const history = useHistory();

  // removed redundant local search state
  const [viewType, setViewType] = useState<OVRItemsListViewType>(
    (localStorage.getItem(ovrListViewTypeKey) as OVRItemsListViewType) ||
      OVRItemsListViewType.Grid
  );

  const {
    value: ovrProjects,
    fetchingOvrProjects,
    totalCount,
    pagination,
    searchingOvrProjects,
    allOvrProjects,
    page,
    pageSize,
    search,
    sort,
  } = useAppSelector((state) => state.ovrProjects);

  const hasNoSearchResultsFound = useMemo(
    () => !fetchingOvrProjects && !!search && ovrProjects.length === 0,
    [fetchingOvrProjects, search, ovrProjects.length]
  );

  const hasOvrProjects = !(!fetchingOvrProjects && allOvrProjects.length === 0);

  const hasNoProjects = useMemo(
    () => !fetchingOvrProjects && allOvrProjects.length === 0,
    [fetchingOvrProjects, allOvrProjects.length]
  );

  const hasMoreOvrProjects =
    hasOvrProjects && pagination?.offset! + pagination?.limit! < totalCount!;

  const isPermittedToLoadMore =
    !fetchingOvrProjects &&
    !searchingOvrProjects &&
    !search &&
    hasMoreOvrProjects;

  const listApiConfig: ListApiConfig = useMemo(
    () => ({
      defaultSort: 'updated_at:desc',
      columns: '*,client.*,createdByUser.*',
      extraStaticParams: { status: 'active' },
      searchParamKey: 'name',
      searchTransformer: (s) => `*${s.toLowerCase()}*`,
    }),
    []
  );

  const updateOVRListViewType = useCallback((type: OVRItemsListViewType) => {
    setViewType(type);
    localStorage.setItem(ovrListViewTypeKey, type);
  }, []);

  const { hasHydrated } = useUrlHydration({
    location,
    controlsFromStore: { page, pageSize, search, sort },
    onHydrate: (urlControls) => dispatch(hydrateFromUrl(urlControls)),
  });

  const fetchOvrThunk = useCallback(
    (params: Record<string, any>) => dispatch(fetchOvrProjects({ params })),
    [dispatch]
  );

  useListFetcher({
    hasHydrated,
    controlsFromStore: { page, pageSize, search, sort },
    config: listApiConfig,
    fetchThunk: fetchOvrThunk,
  });

  const { searchValue, onChange, clear } = useSyncedSearch({
    storeSearchValue: search,
    setStoreSearch: (value) => dispatch(hydrateFromUrl({ search: value })),
    pageSize,
    sort,
    history,
    location,
    onSearch: async (value: string) => {
      const params = buildListParams(
        { page: 1, pageSize, search: value, sort } as OvrProjectsState,
        listApiConfig
      );
      await dispatch(searchOvrProjectsByName({ params }));
    },
  });

  // removed local input sync; useSyncedSearch maintains UI value

  // Skeleton loading
  useEffect(() => {
    if (!!search || ovrProjects.length > 20) return;

    localStorage.setItem(lastOvrProjectsItemLengthKey, `${ovrProjects.length}`);
  }, [ovrProjects.length, search]);

  // Effects replaced by shared hooks

  if (!hasPermission(UserPermissions.OvrProjectsView)) {
    return <NoPermission />;
  }

  return (
    <ViewWrapper
      headerTitle={
        <Space direction="horizontal">
          <Title level={3}>Projects</Title>
          <Badge
            size="default"
            count={totalCount}
            className="total-count-badge"
            overflowCount={999}
          />
        </Space>
      }
      headerSectionRight={
        <OVRHeaderSection
          viewType={viewType}
          searchQuery={searchValue}
          searchingOvrProjects={searchingOvrProjects}
          onViewTypeChange={updateOVRListViewType}
          onSearchChange={onChange}
        />
      }
    >
      <OVRContentRenderer
        searchingOvrProjects={searchingOvrProjects}
        hasNoProjects={hasNoProjects}
        hasNoSearchResultsFound={hasNoSearchResultsFound}
        hasMoreOvrProjects={hasMoreOvrProjects}
        isPermittedToLoadMore={isPermittedToLoadMore}
        fetchingOvrProjects={fetchingOvrProjects}
        ovrProjects={ovrProjects}
        viewType={viewType}
        search={search}
        onSearchClear={clear}
      />
    </ViewWrapper>
  );
};

export default OnlineVirtualResearchView;
